import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { LayoutPresetId } from "@gooner/shared";
import { LAYOUT_PRESETS } from "@gooner/shared";
import { DEFAULT_PLAY_LAYOUT, collectionView } from "../lib/playCollection";
import type { ViewInput } from "../lib/api";
import { MintedScreen } from "./MintedScreen";

/** `?layout=` is user-supplied and may name a preset we've since removed, so
 *  validate it rather than casting — presetRects() has no case for a stale id. */
function layoutFromParam(raw: string | null): LayoutPresetId {
  return LAYOUT_PRESETS.some((p) => p.id === raw)
    ? (raw as LayoutPresetId)
    : DEFAULT_PLAY_LAYOUT;
}

/**
 * Plays a public collection across a preset layout, every region sourced from it.
 *
 * Like PlayRandom, this is a real temporary view on a real device now rather than a View
 * held in this component — see MintedScreen. It still works signed out for any public or
 * Editor's Choice collection, because a temporary view has no owner: holding its id is
 * the whole claim.
 */
export function PlayCollection() {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const layout = layoutFromParam(sp.get("layout"));

  const input = useMemo<ViewInput | null>(() => {
    if (!id) return null;
    const v = collectionView(id, layout);
    return { name: v.name, icon: v.icon, aspect: v.aspect, regions: v.regions };
  }, [layout, id]);

  return <MintedScreen input={input} cacheKey={`collection:${id}:${layout}`} />;
}
